import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
} from "@/components/molecules/responsive-dialog";
import { Button } from "@/components/ui/button";
import { CardContent } from "@/components/ui/card";
import { DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import {
  useWebAuthnCompleteRegistration,
  useWebAuthnRegister,
} from "@/features/auth/hooks/use-webauthn-challenge";
import { useTranslation } from "@/features/utils/i18n";
import { useControlledEffect } from "@/features/utils/use-controlled-effect";
import { startRegistration } from "@simplewebauthn/browser";
import { useState } from "react";
import { toast } from "sonner";

interface WebAuthnRegisterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  title?: string;
  description?: string;
  name?: string; // Predefined name for the device (if any)
  required?: boolean; // If true, shows "Required" messaging
}

export const WebAuthnRegister = ({
  open,
  onOpenChange,
  onSuccess,
  title,
  description,
  required = false,
  name,
}: WebAuthnRegisterProps) => {
  const { t } = useTranslation();
  const [deviceName, setDeviceName] = useState(name || "");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"name" | "authenticating">(
    name ? "authenticating" : "name",
  );

  const registerMutation = useWebAuthnRegister();
  const completeRegistrationMutation = useWebAuthnCompleteRegistration();

  useControlledEffect(() => {
    if (open && name) {
      handleRegister();
    }
  }, []);

  const handleRegister = async () => {
    console.log("Starting WebAuthn registration with device name:", deviceName);
    if (!deviceName.trim()) {
      toast.error(t("security.securityKeys.pleaseEnterDeviceName"));
      return;
    }

    setIsLoading(true);
    setStep("authenticating");

    try {
      // Step 1: Start registration
      const response = await registerMutation.mutateAsync({
        deviceName: deviceName.trim(),
      });

      // Step 2: Use the browser WebAuthn API with SimpleWebAuthn
      const credential = await startRegistration({
        optionsJSON: response.challenge,
      });

      // Step 3: Complete registration
      await completeRegistrationMutation.mutateAsync({
        challengeToken: response.challenge.challengeToken,
        credential,
      });

      // Success toast is handled by the hook
      onSuccess?.();
      onOpenChange(false);
      setDeviceName("");
      if (!name) {
        setStep("name");
      } else {
        onOpenChange(false);
      }
    } catch (error) {
      console.error("WebAuthn registration error:", error);
      let errorMessage = t("security.securityKeys.errors.registrationFailed");

      if (error instanceof Error) {
        if (error.name === "NotAllowedError") {
          errorMessage = t("security.securityKeys.errors.notAllowed");
        } else if (error.name === "NotSupportedError") {
          errorMessage = t("security.securityKeys.errors.notSupported");
        } else if (error.name === "SecurityError") {
          errorMessage = t("security.securityKeys.errors.securityError");
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      toast.error(errorMessage);
      if (!name) {
        setStep("name");
      } else {
        onOpenChange(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (!required) {
      onOpenChange(false);
      setDeviceName("");
      if (!name) {
        setStep("name");
      } else {
        onOpenChange(false);
      }
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={required ? undefined : onOpenChange}
    >
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <DialogTitle>
            {title || t("security.securityKeys.addTitle")}
            {required && (
              <span className="text-destructive"> {t("common.required")}</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === "name" &&
              (description || t("security.securityKeys.addDescription"))}
            {step === "authenticating" &&
              t("security.securityKeys.authenticating")}
          </DialogDescription>
        </ResponsiveDialogHeader>

        <CardContent>
          {step === "name" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="deviceName">
                  {t("security.securityKeys.deviceName")}
                </Label>
                <Input
                  id="deviceName"
                  placeholder={t("security.securityKeys.deviceNamePlaceholder")}
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  disabled={isLoading}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && deviceName.trim()) {
                      handleRegister();
                    }
                  }}
                />
                <p className="text-sm text-muted-foreground">
                  {t("security.securityKeys.deviceNameHelp")}
                </p>
              </div>
            </div>
          )}

          {step === "authenticating" && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center space-y-4">
                <div className="animate-pulse flex items-center justify-center">
                  <Spinner />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("security.securityKeys.waitingAuth")}
                </p>
              </div>
            </div>
          )}
        </CardContent>

        <ResponsiveDialogFooter className="flex gap-2">
          {step === "name" && (
            <>
              {!required && (
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  {t("common.cancel")}
                </Button>
              )}
              <Button
                onClick={handleRegister}
                disabled={!deviceName.trim() || isLoading}
              >
                {isLoading
                  ? t("security.securityKeys.registering")
                  : t("security.securityKeys.register")}
              </Button>
            </>
          )}
          {step === "authenticating" && (
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={required}
            >
              {t("common.cancel")}
            </Button>
          )}
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
